import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Wrench, MapPin, Calendar, Clock, AlertTriangle, CheckCircle, 
  History, Package, FileText, QrCode, ArrowLeft, Play, User,
  Ticket, Settings, ChevronRight, Plus, Eye, BookOpen
} from 'lucide-react';

const EquipmentViewPage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const [equipment, setEquipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tickets');
  const [tickets, setTickets] = useState([]);
  const [revisions, setRevisions] = useState([]);
  const [parts, setParts] = useState([]);
  const [showTakeTicketDialog, setShowTakeTicketDialog] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [takeNotes, setTakeNotes] = useState('');

  useEffect(() => {
    fetchEquipment();
  }, [code]);

  const fetchEquipment = async () => {
    try {
      setLoading(true);
      // Get equipment by code
      const eqResponse = await api.get('/equipment');
      const eq = eqResponse.data.find(e => e.code === code || e.id === code);
      
      if (!eq) {
        toast.error('Echipament negasit');
        navigate('/settings/equipment');
        return;
      }
      
      setEquipment(eq);
      
      // Fetch history
      const historyResponse = await api.get(`/equipment/${eq.id}/history`);
      setTickets(historyResponse.data.tickets || []);
      setRevisions(historyResponse.data.revisions || []);
      
      // Fetch compatible parts (from articles linked to this equipment or category)
      try {
        const articlesResponse = await api.get('/articles');
        // Filter articles that might be relevant (simplified - you could have a proper link)
        setParts(articlesResponse.data.slice(0, 10));
      } catch (e) {
        console.log('No articles found');
      }
      
    } catch (error) {
      console.error('Error fetching equipment:', error);
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  };

  const handleTakeTicket = async () => {
    if (!selectedTicket) return;
    
    try {
      // Accept the ticket and assign to current user
      await api.post(`/tickets/${selectedTicket.id}/accept`, {
        priority_assigned: selectedTicket.priority_requested || 'medium',
        assigned_to: user.id,
        notes: takeNotes
      });
      
      toast.success('Tichet preluat cu succes!');
      setShowTakeTicketDialog(false);
      setSelectedTicket(null);
      setTakeNotes('');
      fetchEquipment();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la preluare');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-blue-100 text-blue-700',
      accepted: 'bg-yellow-100 text-yellow-700',
      in_progress: 'bg-purple-100 text-purple-700',
      resolved: 'bg-green-100 text-green-700',
      closed: 'bg-slate-100 text-slate-700',
      rejected: 'bg-red-100 text-red-700',
      transformed: 'bg-cyan-100 text-cyan-700',
      scheduled: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  const getStatusLabel = (status) => {
    const labels = {
      open: 'Nou',
      accepted: 'Acceptat',
      in_progress: 'In Lucru',
      resolved: 'Rezolvat',
      closed: 'Inchis',
      rejected: 'Respins',
      transformed: 'Transformat',
      scheduled: 'Planificat',
      completed: 'Finalizat',
      cancelled: 'Anulat'
    };
    return labels[status] || status;
  };

  const activeTickets = tickets.filter(t => ['open', 'accepted', 'in_progress'].includes(t.status));
  const resolvedTickets = tickets.filter(t => ['resolved', 'closed', 'transformed'].includes(t.status));

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!equipment) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">Echipament negasit</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-slate-900">{equipment.name}</h1>
              <div className="flex items-center gap-3 text-sm text-slate-500">
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {equipment.location_name || 'N/A'}
                </span>
                <span className="font-mono">{equipment.code}</span>
              </div>
            </div>
            <Button 
              variant="outline"
              onClick={() => navigate(`/knowledge-base?equipment=${equipment.id}`)}
              data-testid="eq-view-kb-btn"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Documente</span>
            </Button>
            <Button 
              className="btn-primary"
              onClick={() => navigate(`/tickets?equipment=${equipment.id}`)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Tichet Nou
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">{activeTickets.length}</p>
                  <p className="text-xs text-slate-500">Tichete Active</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">{resolvedTickets.length}</p>
                  <p className="text-xs text-slate-500">Rezolvate</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-600">{revisions.length}</p>
                  <p className="text-xs text-slate-500">Revizii Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-600">
                    {equipment.installation_date ? 
                      Math.floor((new Date() - new Date(equipment.installation_date)) / (1000 * 60 * 60 * 24 * 365)) : '-'}
                  </p>
                  <p className="text-xs text-slate-500">Ani Vechime</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Active Ticket Alert */}
        {activeTickets.length > 0 && (
          <Card className="mb-6 border-red-200 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="font-semibold text-red-800 mb-2">
                    {activeTickets.length} Tichet{activeTickets.length > 1 ? 'e' : ''} Activ{activeTickets.length > 1 ? 'e' : ''}
                  </h3>
                  {activeTickets.slice(0, 2).map(ticket => (
                    <div key={ticket.id} className="flex items-center justify-between py-2 border-t border-red-200 first:border-t-0">
                      <div>
                        <p className="font-medium text-slate-900">{ticket.title}</p>
                        <p className="text-sm text-slate-600">{ticket.description?.substring(0, 60)}...</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={getStatusColor(ticket.status)}>
                          {getStatusLabel(ticket.status)}
                        </Badge>
                        {ticket.status === 'open' && hasRole('technician', 'manager', 'admin') && (
                          <Button 
                            size="sm" 
                            className="btn-primary"
                            onClick={() => {
                              setSelectedTicket(ticket);
                              setShowTakeTicketDialog(true);
                            }}
                          >
                            <Play className="w-4 h-4 mr-1" />
                            Preia
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="tickets" className="flex items-center gap-2">
              <Ticket className="w-4 h-4" />
              Tichete ({tickets.length})
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <History className="w-4 h-4" />
              Istoric
            </TabsTrigger>
            <TabsTrigger value="parts" className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              Piese
            </TabsTrigger>
            <TabsTrigger value="docs" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Documente
            </TabsTrigger>
            <TabsTrigger value="info" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Info
            </TabsTrigger>
          </TabsList>

          {/* Tickets Tab */}
          <TabsContent value="tickets">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Tichete pentru acest echipament</CardTitle>
              </CardHeader>
              <CardContent>
                {tickets.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">Nu exista tichete</p>
                ) : (
                  <div className="space-y-3">
                    {tickets.map(ticket => (
                      <div 
                        key={ticket.id} 
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer"
                        onClick={() => navigate(`/tickets?id=${ticket.id}`)}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium">{ticket.title}</span>
                            <Badge className={getStatusColor(ticket.status)}>
                              {getStatusLabel(ticket.status)}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-500">
                            {new Date(ticket.created_at).toLocaleDateString('ro-RO')} • 
                            {ticket.assigned_to_name ? ` Asignat: ${ticket.assigned_to_name}` : ' Neasignat'}
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Istoric Complet</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  {/* Timeline */}
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200"></div>
                  
                  <div className="space-y-6 ml-10">
                    {[...tickets, ...revisions]
                      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                      .slice(0, 20)
                      .map((item, index) => (
                        <div key={item.id} className="relative">
                          <div className={`absolute -left-10 w-4 h-4 rounded-full border-2 border-white ${
                            item.status === 'completed' || item.status === 'resolved' || item.status === 'closed'
                              ? 'bg-green-500' 
                              : item.status === 'open' || item.status === 'scheduled'
                              ? 'bg-blue-500'
                              : 'bg-yellow-500'
                          }`}></div>
                          
                          <div className="bg-white p-3 rounded-lg border">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline">
                                {item.revision_type ? 'Revizie' : 'Tichet'}
                              </Badge>
                              <Badge className={getStatusColor(item.status)}>
                                {getStatusLabel(item.status)}
                              </Badge>
                            </div>
                            <p className="font-medium">{item.title || item.description?.substring(0, 50)}</p>
                            <p className="text-sm text-slate-500">
                              {new Date(item.created_at).toLocaleDateString('ro-RO', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                              {item.assigned_to_name && ` • ${item.assigned_to_name}`}
                            </p>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Parts Tab */}
          <TabsContent value="parts">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Piese Compatibile</CardTitle>
              </CardHeader>
              <CardContent>
                {parts.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">Nu exista piese definite</p>
                ) : (
                  <div className="space-y-3">
                    {parts.map(part => (
                      <div key={part.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium">{part.name}</p>
                          <p className="text-sm text-slate-500">
                            Cod: {part.code || 'N/A'} • Stoc: {part.current_stock || 0} {part.unit || 'buc'}
                          </p>
                        </div>
                        <Button variant="outline" size="sm">
                          Solicita
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="docs">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Documente</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-500 text-center py-8">
                  Nu exista documente atasate
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Info Tab */}
          <TabsContent value="info">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Informatii Echipament</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-slate-500">Cod</p>
                      <p className="font-mono font-medium">{equipment.code}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Nume</p>
                      <p className="font-medium">{equipment.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Categorie</p>
                      <p className="font-medium">{equipment.category_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Locatie</p>
                      <p className="font-medium">{equipment.location_name || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-slate-500">Producator</p>
                      <p className="font-medium">{equipment.manufacturer || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Model</p>
                      <p className="font-medium">{equipment.model || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Serie</p>
                      <p className="font-mono font-medium">{equipment.serial_number || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Numar Inventar</p>
                      <p className="font-mono font-medium" data-testid="equipment-inventory-number">{equipment.inventory_number || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Data Instalarii</p>
                      <p className="font-medium">
                        {equipment.installation_date 
                          ? new Date(equipment.installation_date).toLocaleDateString('ro-RO')
                          : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Take Ticket Dialog */}
      <Dialog open={showTakeTicketDialog} onOpenChange={setShowTakeTicketDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Preia Tichetul</DialogTitle>
          </DialogHeader>
          {selectedTicket && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="font-medium">{selectedTicket.title}</p>
                <p className="text-sm text-slate-500">{selectedTicket.description}</p>
              </div>
              <div className="space-y-2">
                <Label>Note (optional)</Label>
                <Textarea
                  value={takeNotes}
                  onChange={(e) => setTakeNotes(e.target.value)}
                  placeholder="Adauga note despre preluare..."
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTakeTicketDialog(false)}>
              Anuleaza
            </Button>
            <Button className="btn-primary" onClick={handleTakeTicket}>
              <Play className="w-4 h-4 mr-2" />
              Preia Tichetul
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EquipmentViewPage;
